/**
 * 配信 API（統合面 `/api/v1/delivery`）の zod 契約。
 * `docs/CMS_GAP_AND_API.md` §4.6 と `docs/PRODUCT_DIFFERENTIATION.md` §5.3 が唯一の正。
 *
 * **`content-api.ts` から分離した理由。**
 * 同居していたときは「コンテンツの契約を直したつもりで配信の契約も動く」状態で、
 * 面（`/api/v1/content` と `/api/v1/delivery`）が別なのにファイルが 1 つだった。
 * 統合面のトークンはスコープが面ごとに分かれる（`content.*` / `delivery.*`）ので、
 * **ファイルの境界を面の境界に合わせておく**ほうが権限の穴に気付きやすい。
 * 共通の部品（`integrationIdSchema` / ページング / 冪等キー）は
 * `content-api.ts` を唯一の出典として import する。写経しないこと。
 *
 * この面の設計上の核心が 3 つある。
 *
 * 1. **`start` / `resume` が存在しない。** スケジュール投入は `awaiting_release` 止まりで、
 *    解禁は portal（人間のセッション）でしかできない。10 万通は撤回できない。
 * 2. **本文が API を通らない。** キャンペーンはコンテンツを ID で参照するだけ。
 * 3. **同意証跡なしで宛先を作れない。** `upsertContactSchema.optin` は必須で、
 *    ここを optional にした瞬間に特定電子メール法 3 条 1 項のゲートが素通りする。
 */
import { z } from 'zod';
import { CAMPAIGN_STATUS, CONTACT_OPTIN_METHOD } from '../enums.js';
import {
  externalRefSchema,
  integrationDateTimeSchema,
  integrationIdSchema,
} from './content-api.js';

const id = integrationIdSchema;
const isoDateTime = integrationDateTimeSchema;

/* ============================================================== キャンペーン */
export const campaignSummarySchema = z
  .object({
    id,
    name: z.string(),
    status: z.enum(CAMPAIGN_STATUS),
    listId: id.nullable(),
    sendingStreamId: id.nullable(),
    contentDocumentId: id.nullable(),
    contentRevision: z.number().int().positive().nullable(),
    scheduledAt: isoDateTime.nullable(),
    releasedAt: isoDateTime.nullable(),
    externalRef: z.string().nullable(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
  })
  .strict();

export const createCampaignSchema = z
  .object({
    name: z.string().min(1).max(200),
    listId: id,
    sendingStreamId: id,
    /** **コンテンツは ID 参照のみ。** 本文をここに書く経路を作らない。 */
    contentDocumentId: id,
    contentRevision: z.number().int().positive().optional(),
    segmentRule: z.record(z.unknown()).optional(),
    externalRef: externalRefSchema.optional(),
  })
  .strict();

export const updateCampaignSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    listId: id.optional(),
    sendingStreamId: id.optional(),
    contentDocumentId: id.optional(),
    contentRevision: z.number().int().positive().optional(),
    segmentRule: z.record(z.unknown()).optional(),
  })
  .strict();

/**
 * `POST /delivery/campaigns/:id/schedule`。
 * **返る状態は `awaiting_release`。** `scheduled` にするのは portal の解禁だけ。
 */
export const scheduleCampaignSchema = z.object({ scheduledAt: isoDateTime }).strict();

export const scheduleCampaignResponseSchema = z
  .object({
    campaignId: id,
    status: z.literal('awaiting_release'),
    scheduledAt: isoDateTime,
    /** 解禁は人間が行うことを応答で明示する（自動化できないことを黙らない）。 */
    releaseRequired: z.literal(true),
    releaseUrl: z.string(),
  })
  .strict();

/** F5 プリフライトの応答。**`content_artifacts` のメタだけを読み、本文は読まない**。 */
export const campaignValidationSchema = z
  .object({
    ok: z.boolean(),
    blocks: z.array(z.object({ code: z.string(), message: z.string(), hint: z.string().nullable() }).strict()),
    warnings: z.array(z.object({ code: z.string(), message: z.string(), hint: z.string().nullable() }).strict()),
    checkedAt: isoDateTime,
  })
  .strict();

/** キャンペーンのレポート。**受信者単位の一覧は返さない**（PII の一括持ち出しになる）。 */
export const campaignReportSchema = z
  .object({
    campaignId: id,
    status: z.enum(CAMPAIGN_STATUS),
    recipientCount: z.number().int().nonnegative(),
    stats: z
      .object({
        sent: z.number().int().nonnegative(),
        delivered: z.number().int().nonnegative(),
        hardBounced: z.number().int().nonnegative(),
        softBounced: z.number().int().nonnegative(),
        complained: z.number().int().nonnegative(),
        unsubscribed: z.number().int().nonnegative(),
        /**
         * **相手 MTA へ渡す前に落ちた通数**（SMTP 注入の失敗）。
         * `sent` には含めない。バウンス（渡した後に返ってきた分）とは
         * 原因も対処も違うので、同じ数字に丸めない。
         */
        failed: z.number().int().nonnegative(),
        clicked: z.number().int().nonnegative().nullable(),
      })
      .strict(),
    startedAt: isoDateTime.nullable(),
    completedAt: isoDateTime.nullable(),
  })
  .strict();

/* ------------------------------------------------------------ 宛先とコンタクト */

export const contactSchema = z
  .object({
    id,
    email: z.string(),
    displayName: z.string().nullable(),
    status: z.string(),
    attributes: z.record(z.string()).default({}),
    trackingOptOut: z.boolean(),
    createdAt: isoDateTime,
  })
  .strict();

/**
 * コンタクトの upsert。
 *
 * **`optin` は必須。** オプトイン証跡（`optin_records`）を作らない経路を残すと、
 * 「同意の無い宛先が 1 件でもあれば送信できない」というゲート（特定電子メール法 3 条 1 項）が
 * 素通りする。外部 CRM から取り込むときも証跡を必ず一緒に渡させる。
 */
export const upsertContactSchema = z
  .object({
    email: z.string().email().max(320),
    displayName: z.string().max(200).optional(),
    attributes: z.record(z.string().max(500)).optional(),
    optin: z
      .object({
        // 値の出典は `CONTACT_OPTIN_METHOD`（enums.ts）。ここに literal を書くと
        // DB の `optin_records.method` と静かにずれる。
        method: z.enum(CONTACT_OPTIN_METHOD),
        occurredAt: isoDateTime,
        /** 同意の証跡（フォーム版・同意文言のハッシュ・取得元 URL など）。 */
        evidence: z.record(z.unknown()),
      })
      .strict(),
  })
  .strict();

/**
 * コンタクトの部分更新（統合面 `PATCH /lists/:listId/contacts/:contactId`）。
 *
 * **`status` を `subscribed` へ戻せるのは証跡がある場合だけ**（実装が 409 で拒む）。
 * ここを素通しにすると、解除された宛先を API で購読済みに書き戻すだけで
 * `optin_records` のゲートを迂回できてしまう。
 */
export const updateContactSchema = z
  .object({
    displayName: z.string().max(200).nullable().optional(),
    attributes: z.record(z.string().max(500)).optional(),
    status: z.enum(['subscribed', 'unsubscribed', 'cleaned']).optional(),
  })
  .strict();

/* ============================================================ CSV の取り込み */

/**
 * 一括取り込み（PD §5.3 の 3 ステップ: 起票 + presign → 検査 → 適用）。
 *
 * **`optin` は単票の upsert と同じく必須。** 一括経路にだけ抜け道を作ると、
 * 1 件ずつでは絶対に起きない「証跡なしの名簿が 10 万件」が成立する。
 */
export const createContactImportSchema = z
  .object({
    optin: z
      .object({
        method: z.enum(CONTACT_OPTIN_METHOD),
        occurredAt: isoDateTime,
        evidence: z.record(z.unknown()),
      })
      .strict(),
  })
  .strict();

/** **`objectKey` は返さない。** S3 のキーを契約者に見せる必要が無い。 */
export const contactImportSchema = z
  .object({
    id,
    listId: id,
    sourceKind: z.string(),
    status: z.string(),
    totalRows: z.number().int().nonnegative().nullable(),
    acceptedRows: z.number().int().nonnegative().nullable(),
    rejectedRows: z.number().int().nonnegative().nullable(),
    optinMethod: z.enum(CONTACT_OPTIN_METHOD),
    optinEvidence: z.record(z.unknown()),
    /** 検査の結果（行番号つきの却下理由）。適用前はここだけを見て判断する。 */
    report: z.unknown().nullable(),
    appliedAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
  })
  .strict();

export const suppressionSchema = z
  .object({
    id,
    email: z.string(),
    reason: z.string(),
    scope: z.string(),
    sourceCampaignId: id.nullable(),
    releasedAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
  })
  .strict();

export const createSuppressionSchema = z
  .object({
    email: z.string().email().max(320),
    reason: z.enum(['manual', 'complaint', 'hard_bounce', 'external_unsubscribe']),
    note: z.string().max(500).optional(),
  })
  .strict();

/**
 * サプレッションの削除で拒否する理由。
 * `unsubscribe` は**受信者本人の意思**なので、契約者が消せてはならない（403）。
 * 解除できるのは本人がダブルオプトインで再購読したときだけ。
 */
export const SUPPRESSION_REASONS_LOCKED = ['unsubscribe'] as const;

export function isSuppressionReleasableByOrg(reason: string): boolean {
  return !(SUPPRESSION_REASONS_LOCKED as readonly string[]).includes(reason);
}
